import cs from 'classnames'
import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import Spinner from '@/ui/icons/spinner'

type IframeModalProps = {
  modalTitle?: string
  src: string
  onClose?: (() => void) | string
  onCompleted?: (() => void) | string
}

type IframeModalLinkProps = {
  href: string
  label: string
  className?: string
  modalTitle?: string
  src?: string
  onClose?: (() => void) | string
  onCompleted?: (() => void) | string
}

const handleCallback = (callback: (() => void) | string | undefined) => {
  if (callback) {
    if (typeof callback === 'string') {
      document.location.href = callback
    } else {
      callback()
    }
  }
}

export const IframeModalLink = ({
  href,
  label,
  className,
  modalTitle,
  src,
  onClose,
  onCompleted,
}: IframeModalLinkProps) => {
  const [open, setOpen] = useState(false)

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    setOpen(true)
    return false
  }

  const handleClose = () => {
    setOpen(false)
    handleCallback(onClose)
  }

  const handleCompleted = () => {
    setOpen(false)
    handleCallback(onCompleted)
  }

  return (
    <>
      <a href={href} className={className} onClick={handleClick}>
        {label}
      </a>
      {open
        ? createPortal(
            <IframeModal
              modalTitle={modalTitle ?? label}
              src={src ?? href}
              onClose={handleClose}
              onCompleted={handleCompleted}
            />,
            document.body
          )
        : null}
    </>
  )
}

export const IframeModal = ({
  modalTitle,
  src,
  onClose,
  onCompleted,
}: IframeModalProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null)

  const [isLoaded, setIsLoaded] = useState(false)

  useEffect(() => {
    const handleLoad = () => {
      setIsLoaded(true)
    }

    const handleMessage = (event: MessageEvent) => {
      if (!event.isTrusted || !src.startsWith(event.origin)) {
        return
      }
      if (event.data === 'iframe-completed' && onCompleted) {
        handleCallback(onCompleted)
      } else if (event.data === 'iframe-close' && onClose) {
        handleCallback(onClose)
      }
    }

    const iframe = iframeRef.current
    if (iframe) {
      iframe.addEventListener('load', handleLoad)
      window.addEventListener('message', handleMessage)
      return () => {
        iframe.removeEventListener('load', handleLoad)
        window.removeEventListener('message', handleMessage)
      }
    }
  }, [iframeRef.current])

  return (
    <div className="iframe-modal" onClick={() => handleCallback(onClose)}>
      <div
        onClick={e => e.stopPropagation()}
        className={cs('iframe-modal__content', {
          'iframe-modal__content--with-header': !!modalTitle,
        })}
      >
        {modalTitle ? (
          <div className="iframe-modal__header">
            <h2 className="iframe-modal__title">{modalTitle}</h2>
          </div>
        ) : null}
        <div
          className="iframe-modal__loading-indicator"
          style={{ display: isLoaded ? 'none' : '' }}
        >
          <Spinner width={40} />
        </div>
        <iframe
          style={{ visibility: isLoaded ? 'visible' : 'hidden' }}
          ref={iframeRef}
          src={src}
          className="iframe-modal__frame"
        />
      </div>
    </div>
  )
}
